import * as Yup from 'yup';
import { App } from '../../App';
import {
  Button,
  Card,
  CheckboxActor,
  Heading,
  Link,
  Modal,
  Text,
  Title,
} from 'components';
import { CheckIcon, CheckmarkIcon, QuestionIcon } from 'components/icons';
import { createTransLinksComponents } from '../../utils/transLinksComponents';
import { formatApplicationSummary } from '../../formatters/formatApplicationSummary';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { routes } from '../../routes';
import { AffiliateSection as Section } from './section';
import { Table } from 'components/molecules/Table/Table';
import { useApplicationSummary } from '../../hooks/useApplicationSummary';
import { useFormik } from 'formik';
import { useGetClientApplication } from '../../api';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';
import useClient from '../../hooks/useClient';

import { Trans, useTranslation } from 'react-i18next';
import { useProduct } from 'hooks/useProduct';
import { useWebcode } from '../../hooks/useWebcode';
import classes from 'classnames';
import es from '../../localizations/es';
import layout from 'scss/layout.module.scss';
import QuestionsAccordion from 'components/molecules/QuestionsAccordion/QuestionsAccordion';
import styles from './FirstLoan.module.scss';
import useIovation from '../../hooks/useIovation';

type QuestionAnswer = { question: string; answer: string };

export default function AffiliateFirstLoan() {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { fetch: fetchPutClientSettings } = App.useApi(
    'client/settings',
    'PUT',
  );
  const { fetch: fetchPostAcceptAgreement } = App.useApi(
    'client/accept-latest-agreement-version',
    'POST',
  );
  const { isWebCodeRequired } = useWebcode();
  const apiGetClientApplication = useGetClientApplication();
  const { amount, interest, repayableAmount, repaymentDate } =
    formatApplicationSummary(useApplicationSummary());
  const { client } = useClient();
  useIovation();
  const i18next = useTranslation();
  const { valueRelatedToProduct } = useProduct();
  const valueRelatedToProductParsed = valueRelatedToProduct as
    | 'items_vivus'
    | 'items_vivusonline';

  const [showModal, setShowModal] = useState(false);

  const handleModal = () => setShowModal(!showModal);

  const validationSchema = Yup.object().shape({
    terms: Yup.bool().isTrue(i18next.t('validation:agreement_required')),
  });

  const onSubmit = async ({
    auth_company: acceptNews,
    auth_me: acceptDataSharing,
    terms,
  }: any) => {
    try {
      setLoading(true);

      if (terms) {
        await fetchPostAcceptAgreement({
          body: {},
        });
        await fetchPutClientSettings({
          body: {
            acceptDataSharing,
            acceptNews,
          },
        });
        if (await isWebCodeRequired()) {
          navigate(routes.FirstLoan_PhoneVerification);
          return;
        }

        if (!apiGetClientApplication.data?.confirmed) {
          navigate(routes.FirstLoan_PhoneVerification_Check);
          return;
        }

        navigate(routes.FirstLoan_OnlineBank_Verify);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      auth_me: false,
      auth_company: false,
      terms: false,
    },
    onSubmit,
    validationSchema,
  });

  const onSelectAll = () => {
    const nextValue = !Object.values(formik.values).every(
      (valueAgreement: boolean) => valueAgreement,
    );

    formik.setValues({
      auth_company: nextValue,
      auth_me: nextValue,
      terms: nextValue,
    });
  };

  const faqItemsKey = ['items_vivus', 'items_vivusonline'].includes(
    valueRelatedToProductParsed,
  )
    ? valueRelatedToProductParsed
    : 'items_vivus';

  const content = {
    selectAll: i18next.t('affiliates:firstapplication.selectAll'),
    title: i18next.t('affiliates:firstapplication.title'),
    title_mobile: i18next.t('affiliates:firstapplication.title_mobile', {
      name: client?.firstName ? ` ${client.firstName}` : '',
    }),
    sub_title: i18next.t('affiliates:firstapplication.sub_title'),
    table_title: i18next.t('affiliates:firstapplication.table.title'),
    table_amount_request: i18next.t(
      'affiliates:firstapplication.table.amount_request',
    ),
    table_interests: i18next.t('affiliates:firstapplication.table.interests'),
    table_return_total: i18next.t(
      'affiliates:firstapplication.table.return_total',
    ),
    table_return_date: i18next.t(
      'affiliates:firstapplication.table.return_date',
    ),
    table_sub_title: i18next.t('affiliates:firstapplication.table.sub_title'),
    authorization_company: i18next.t(
      'affiliates:firstapplication.authorization_company',
    ),
    authorization_me: i18next.t('affiliates:firstapplication.authorization_me'),
    terms_condition: i18next.t('affiliates:firstapplication.terms_condition'),
    button: i18next.t('affiliates:firstapplication.button'),
    button_sub_title: i18next.t('affiliates:firstapplication.button_sub_title'),
    faq_header: i18next.t('affiliates:firstapplication.faq.header'),
    faq_items: es.affiliates.firstapplication.faq[faqItemsKey].map(
      (item, index) => {
        const items: QuestionAnswer[] = i18next.t(
          `affiliates:firstapplication.faq.${faqItemsKey}`,
          {
            returnObjects: true,
          },
        ) as unknown as QuestionAnswer[];

        return {
          question: items[index].question,
          answer: items[index].answer,
        };
      },
    ),
    modal_title: i18next.t('affiliates:firstapplication.modal.title'),
    modal_text: i18next.t('affiliates:firstapplication.modal.text'),
    modal_button: i18next.t('affiliates:firstapplication.modal.button'),
  };

  const values = {
    amount: `${amount} ${i18next.t('common:currencySymbol')}`,
    interest: `${interest} ${i18next.t('common:currencySymbol')}`,
    repayableAmount: `${repayableAmount} ${i18next.t('common:currencySymbol')}`,
    repaymentDate,
  };

  const items = [
    {
      label: content.table_amount_request,
      value: values.amount,
    },
    {
      label: content.table_interests,
      value: values.interest,
    },
    {
      label: content.table_return_total,
      value: values.repayableAmount,
    },
    {
      label: content.table_return_date,
      value: values.repaymentDate,
    },
    {
      label: content.table_sub_title,
      value: '',
    },
  ];

  return (
    <>
      <Frame
        {...frameConfig}
        hideEditButton
        bodyWrapperClassName={classes(layout['width-lg'], styles.frame)}
        sectionID={Section.Welcome}
      >
        <div className={layout['frame-x-padding']}>
          <Title
            className={styles['welcome-title']}
            icon={<CheckmarkIcon />}
            text={content.title_mobile}
            iconClassName={styles['welcome-title-icon']}
          />
        </div>
        <Card className={styles.card}>
          <Table
            className={styles.table}
            title={content.table_title}
            items={items}
          />
        </Card>
        <div className={layout['frame-x-padding']}>
          <form onSubmit={formik.handleSubmit} className={styles.form}>
            <Button
              leftIcon={<CheckIcon />}
              loading={loading}
              fill={true}
              className={classes(styles['select-all-button'])}
              onClick={onSelectAll}
              outlined
            >
              {content.selectAll}
            </Button>
            <div className={styles['checkboxes-wrapper']}>
              <CheckboxActor
                labelClassName={styles.checkbox}
                formik={formik}
                name="auth_company"
                textHTML={content.authorization_company}
              />
              <CheckboxActor
                labelClassName={styles.checkbox}
                formik={formik}
                name="auth_me"
                textHTML={content.authorization_me}
              />
              <CheckboxActor
                labelClassName={styles.checkbox}
                formik={formik}
                name="terms"
                text={
                  <Trans
                    i18nKey="affiliates:firstapplication.terms_condition"
                    components={createTransLinksComponents(handleModal)}
                  />
                }
              />
            </div>
            <Button
              loading={loading}
              type="submit"
              fill={true}
              className={classes(styles['submit-button'], layout['width-sm'])}
            >
              {content.button}
            </Button>
          </form>
          <Link
            href="#"
            onClick={() => setIsModalOpened(true)}
            className={styles['button-sub-title']}
          >
            {content.button_sub_title}
          </Link>
          <Heading
            level="h2"
            text={content.faq_header}
            weight="medium"
            className={styles['faq-title']}
          />
          <div className={styles['faq-accordion']}>
            <QuestionsAccordion list={content.faq_items} />
          </div>
        </div>
      </Frame>
      <Modal
        isOpen={isModalOpened}
        onClose={() => setIsModalOpened(false)}
        isMobileFullWidth
      >
        <Title
          forceColumn={true}
          icon={<QuestionIcon />}
          text={content.modal_title}
        />
        <Text text={content.modal_text} className={styles['modal-text']} />
        <Button onClick={() => setIsModalOpened(false)}>
          {content.modal_button}
        </Button>
      </Modal>
      {showModal && <LegalTermsModal onClose={handleModal} />}
    </>
  );
}
